import { useState, useEffect, useRef } from "react";
import { Divider } from "@mui/material";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import axios from "axios";
import Slider from "react-slick";
import NavbarCharity from "../layout/NavbarCharity";
import styles from "./charity_page2.module.sass";
import { NextArrow, PrevArrow } from "../components/sliderArrow";
import { Title } from "../components/charity/title";
import FooterCharity from "../layout/FooterCharity";
import ImageModal from "../components/ImageModal";
import { renderMedia, isVideoFile, isYouTubeUrl } from "../utils/mediaUtils";

function Charity2() {
  const [charityID, setCharityID] = useState(0);
  const [articleContent, setArticleContent] = useState([]);
  const [activityListContent, setActivityListContent] = useState([]);
  const [pagePath, SetPagePath] = useState("");
  const [currentIndexList, setCurrentIndexList] = useState([]);
  const mainSliderContainerRefs = useRef([]);

  // 主/縮圖輪播參考（每個 activity 一組）
  const nav1Refs = useRef([]);
  const nav2Refs = useRef([]);

  // 放大檢視狀態
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [modalImageSrc, setModalImageSrc] = useState("");
  const [modalImageAlt, setModalImageAlt] = useState("");
  const [activeActivityIndex, setActiveActivityIndex] = useState(0);
  let location = useLocation();

  useEffect(() => {
    let searchParams = new URLSearchParams(location.search);
    let id = searchParams.get("id");
    setCharityID(id * 1);
    SetPagePath(id);
  }, [location.search]);

  useEffect(() => {
    if (charityID != 0) {
      axios
        .post("/api/website/getCharityActivity", { id: charityID })
        .then((response) => {
          document.title = response.data.data.data.title;
          setArticleContent(response.data.data.data);
        })
        .catch((error) => {
          console.error("Error fetching article content:", error);
        });
      axios
        .post("/api/website/getCharityActivityList", { id: charityID })
        .then((response) => {
          setActivityListContent(response.data.data.activityList);
        })
        .catch((error) => {
          console.error("Error fetching article content:", error);
        });
    }
  },[charityID])

  // 初始化每個 activity 的主輪播索引
  useEffect(() => {
    const init = Array.isArray(activityListContent) ? activityListContent.map(() => 0) : [];
    setCurrentIndexList(init);
  }, [activityListContent]);

  // 當各主輪播索引變更時，控制各輪播中的 YouTube 自動播放/暫停
  useEffect(() => {
    (mainSliderContainerRefs.current || []).forEach((container) => {
      if (!container) return;
      // 暫停所有 iframe YouTube
      const iframes = container.querySelectorAll('iframe[src*="youtube.com/embed"]');
      iframes.forEach((ifr) => {
        try {
          ifr.contentWindow?.postMessage(JSON.stringify({ event: "command", func: "pauseVideo", args: [] }), "*");
        } catch (e) { void e; }
      });
      // 播放目前的
      const currentIframe = container.querySelector('.slick-current iframe[src*="youtube.com/embed"]');
      if (currentIframe) {
        try {
          currentIframe.contentWindow?.postMessage(JSON.stringify({ event: "command", func: "mute", args: [] }), "*");
          currentIframe.contentWindow?.postMessage(JSON.stringify({ event: "command", func: "playVideo", args: [] }), "*");
        } catch (e) { void e; }
      }
    });
  }, [currentIndexList]);

  // 監聽 YouTube 播放狀態，結束後對應輪播自動切到下一張
  useEffect(() => {
    const onMessage = (event) => {
      let data = null;
      try {
        data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch { data = null; }
      if (!data || data.event !== "onStateChange" || data.info !== 0) return; // 0: ended
      const containers = mainSliderContainerRefs.current || [];
      for (let i = 0; i < containers.length; i += 1) {
        const container = containers[i];
        if (!container) continue;
        const iframes = container.querySelectorAll('iframe[src*="youtube.com/embed"]');
        let isFromHere = false;
        iframes.forEach((ifr) => {
          if (isFromHere) return;
          try {
            if (ifr.contentWindow === event.source) isFromHere = true;
          } catch (e) { void e; }
        });
        if (isFromHere) {
          const slider = nav1Refs.current?.[i];
          if (slider && typeof slider.slickNext === 'function') {
            slider.slickNext();
          }
          break;
        }
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);
  // 放大圖控制
  function openImageModal(activityIndex) {
    const list = activityListContent?.[activityIndex]?.albumList || [];
    const currentIdx = currentIndexList?.[activityIndex] ?? 0;
    const currentItem = list[currentIdx] || {};
    setActiveActivityIndex(activityIndex);
    setModalImageIndex(currentIdx);
    setModalImageSrc(currentItem.image || "");
    setModalImageAlt(currentItem.title || "");
    setIsImageModalOpen(true);
  }

  function closeImageModal() {
    setIsImageModalOpen(false);
  }

  function handlePreviousImage() {
    const list = activityListContent?.[activeActivityIndex]?.albumList || [];
    if (list.length === 0) return;
    const idx = (modalImageIndex - 1 + list.length) % list.length;
    setModalImageIndex(idx);
    setModalImageSrc(list[idx]?.image || "");
    setModalImageAlt(list[idx]?.title || "");
  }

  function handleNextImage() {
    const list = activityListContent?.[activeActivityIndex]?.albumList || [];
    if (list.length === 0) return;
    const idx = (modalImageIndex + 1) % list.length;
    setModalImageIndex(idx);
    setModalImageSrc(list[idx]?.image || "");
    setModalImageAlt(list[idx]?.title || "");
  }

  return (
    <>
      <NavbarCharity path={pagePath} charityName={articleContent.title} />
      <main className="relative">
        <section className="h-[250px] m-auto page-title flex items-center justify-center">
          <Title title={"最新消息"} subTitle={"Latest News"} />
        </section>
        <section className="max-w-[1300px] mx-auto w-[90%] mobile:max-w-[500px] mobile:w-[85%]">
          {activityListContent.map((item, index) => (
            <div key={"list" + index}>
              <div className="flex flex-row my-10 gap-14 mobile:flex-col mobile:gap-4 mobile:my-7">
                <div
                  className="w-[600px] table:w-[450px] mobile:w-full"
                  ref={(el) => { if (el) mainSliderContainerRefs.current[index] = el; }}
                >
                  <Slider
                    ref={(slider) => { if (slider) nav1Refs.current[index] = slider; }}
                    asNavFor={nav2Refs.current[index]}
                    focusOnSelect={false}
                    className="m-auto small-slider"
                    fade={true}
                    accessibility={false}
                    prevArrow={<PrevArrow customClassName={styles.prevArrow} />}
                    nextArrow={<NextArrow customClassName={styles.nextArrow} />}
                    beforeChange={(oldI, itemIndex) => {
                      const nextList = Array.isArray(currentIndexList) ? [...currentIndexList] : [];
                      nextList[index] = itemIndex;
                      setCurrentIndexList(nextList);
                    }}
                  >
                    {item.albumList.map((contentItem, contentIndex) => (
                      <div key={`AlbumSlider${contentIndex}`}>
                        <div
                          className="w-full aspect-[16/9] bg-black relative overflow-hidden cursor-pointer"
                          onClick={() => {
                            const currentIdx = currentIndexList?.[index] ?? 0;
                            const currentSrc = item.albumList?.[currentIdx]?.image || "";
                            if (currentSrc && !isVideoFile(currentSrc) && !isYouTubeUrl(currentSrc)) {
                              openImageModal(index);
                            }
                          }}
                        >
                          {renderMedia(
                            contentItem.image,
                            contentItem.title || "",
                            "absolute top-0 bottom-0 left-0 right-0 w-auto h-full m-auto",
                            {},
                            false,
                            (currentIndexList?.[index] ?? 0) === contentIndex,
                            null,
                            false
                          )}
                        </div>
                      </div>
                    ))}
                  </Slider>
                  {Array.isArray(item.albumList) && item.albumList.length > 0 && (
                    <div className="mt-4">
                      <Slider
                        asNavFor={nav1Refs.current[index]}
                        ref={(slider) => { if (slider) nav2Refs.current[index] = slider; }}
                        slidesToShow={Math.min(6, item.albumList.length)}
                        infinite={item.albumList.length > 6}
                        centerMode={item.albumList.length > 6}
                        swipeToSlide={true}
                        focusOnSelect={true}
                        accessibility={false}
                        beforeChange={(oldI, newI) => {
                          const nextList = Array.isArray(currentIndexList) ? [...currentIndexList] : [];
                          nextList[index] = newI;
                          setCurrentIndexList(nextList);
                        }}
                        className={"center"}
                        prevArrow={null}
                        nextArrow={null}
                        responsive={[
                          {
                            breakpoint: 1350,
                            settings: {
                              slidesToShow: Math.min(5, item.albumList.length),
                              infinite: item.albumList.length > 5,
                              centerMode: item.albumList.length > 5,
                            },
                          },
                          {
                            breakpoint: 991,
                            settings: {
                              slidesToShow: Math.min(4, item.albumList.length),
                              infinite: item.albumList.length > 4,
                              centerMode: item.albumList.length > 4,
                            },
                          },
                          {
                            breakpoint: 766,
                            settings: {
                              slidesToShow: Math.min(3, item.albumList.length),
                              infinite: item.albumList.length > 3,
                              centerMode: item.albumList.length > 3,
                            },
                          },
                          {
                            breakpoint: 375,
                            settings: {
                              slidesToShow: Math.min(2, item.albumList.length),
                              infinite: item.albumList.length > 2,
                              centerMode: item.albumList.length > 2,
                            },
                          },
                        ]}
                      >
                        {item.albumList.map((thumbItem, tIndex) => (
                          <div key={`AlbumThumb${tIndex}`}>
                            <div className="aspect-[720/483] w-full relative overflow-hidden">
                              {renderMedia(
                                thumbItem.image,
                                thumbItem.title || "",
                                "absolute top-0 left-0 bottom-0 right-0 w-auto h-full m-auto",
                                {},
                                false,
                                false,
                                null,
                                false
                              )}
                            </div>
                          </div>
                        ))}
                      </Slider>
                    </div>
                  )}
                </div>
                <div className="flex-1 [*&>:not(:last-child)]:mb-6 [*&>:not(:last-child)]:table:mb-4 [*&>:not(:last-child)]:mobile:mb-3">
                  <p className="text-3xl font-normal text-[#a93230] table:text-2xl mobile:text-xl">
                    {item.startDate ? item.startDate.substring(0, 10) : ""}{" "}
                    {item.endDate ? " ~ " + item.endDate.substring(0, 10) : ""}
                  </p>
                  <p className="text-3xl font-normal text-[#a93230] table:text-2xl mobile:text-xl">
                    {item.name}
                  </p>
                  <p className="text-xl font-normal">
                    {item.content}
                  </p>
                  {
                    item.isDonate && (
                      <a
                        className="inline-block text-xl bg-[#FFCC06] py-3 px-9 font-normal rounded text-black hover:bg-[#ff8c00] duration-100"
                        role="button"
                        href={"/charity4?id=" + charityID + "&activityID=" + item.id}
                      >
                        我要捐款
                      </a>
                    )
                  }
                </div>
              </div>
              <Divider />
            </div>
          ))}
        </section>
      </main>
      <FooterCharity />
      <ImageModal
        isOpen={isImageModalOpen}
        imageSrc={modalImageSrc}
        imageAlt={modalImageAlt}
        onClose={closeImageModal}
        images={activityListContent?.[activeActivityIndex]?.albumList || []}
        currentIndex={modalImageIndex}
        onPrevious={handlePreviousImage}
        onNext={handleNextImage}
      />
    </>
  );
}

function NewsNextArrow(props) {
  const { className, style, onClick } = props;
  return (
    <div
      className={className}
      style={{
        ...style,
        display: "block",
        marginRight: "-25px",
        width: "50px",
        height: "50px",
        zIndex: 999,
      }}
      onClick={onClick}
    />
  );
}

NewsNextArrow.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  onClick: PropTypes.func,
};

function NewsPrevArrow(props) {
  const { className, style, onClick } = props;
  return (
    <div
      className={className}
      style={{
        ...style,
        display: "block",
        marginLeft: "-25px",
        width: "50px",
        height: "50px",
        zIndex: 999,
      }}
      onClick={onClick}
    />
  );
}

NewsPrevArrow.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  onClick: PropTypes.func,
};

export default Charity2;

